import * as React from 'react'
import {
  clampPercent,
  formatPercentDisplay,
  isAcceptableZoomPercent,
  parsePercentInput,
} from '../helpers/zoom-percent'
import type { ZoomMenuOption } from '../types/zoom-menu-option'

type UseZoomCommitArgs = {
  menuOptions: ZoomMenuOption[]
  minPercent: number
  maxPercent: number
  onZoomPercentCommit: (percent: number) => void
  onActualSize: () => void
  onZoomFit: () => void
  onZoomToSelection?: () => void
  fitToSpreadDisabled: boolean
  zoomSelectionRowDisabled: boolean
  draft: string
  setDraft: (draft: string) => void
  setIsDirty: (dirty: boolean) => void
  dirtyFromInputChangeRef: React.MutableRefObject<boolean>
  resetDraftFromProps: () => void
  flashInvalidEntryTooltip: () => void
  setOpen: (open: boolean) => void
  setHighlightIndex: (index: number) => void
  inputRef: React.RefObject<HTMLInputElement | null>
}

/** Applying a zoom: a typed draft, a preset, or one of the command rows (actual size, fit, selection). */
export function useZoomCommit({
  menuOptions,
  minPercent,
  maxPercent,
  onZoomPercentCommit,
  onActualSize,
  onZoomFit,
  onZoomToSelection,
  fitToSpreadDisabled,
  zoomSelectionRowDisabled,
  draft,
  setDraft,
  setIsDirty,
  dirtyFromInputChangeRef,
  resetDraftFromProps,
  flashInvalidEntryTooltip,
  setOpen,
  setHighlightIndex,
  inputRef,
}: UseZoomCommitArgs) {
  const commitPercent = React.useCallback(
    (percent: number) => {
      const clamped = clampPercent(percent, minPercent, maxPercent)

      onZoomPercentCommit(clamped)
      setIsDirty(false)
      dirtyFromInputChangeRef.current = false
      setDraft(formatPercentDisplay(clamped, minPercent, maxPercent))
      setOpen(false)
      setHighlightIndex(-1)
    },
    [
      dirtyFromInputChangeRef,
      maxPercent,
      minPercent,
      onZoomPercentCommit,
      setDraft,
      setHighlightIndex,
      setIsDirty,
      setOpen,
    ]
  )

  const commitDraft = React.useCallback(() => {
    if (!isAcceptableZoomPercent(draft, minPercent, maxPercent)) {
      resetDraftFromProps()
      flashInvalidEntryTooltip()
      return
    }

    commitPercent(parsePercentInput(draft)!)
  }, [commitPercent, draft, flashInvalidEntryTooltip, maxPercent, minPercent, resetDraftFromProps])

  const activateOptionIndex = React.useCallback(
    (index: number) => {
      const opt = menuOptions[index]

      if (!opt) return

      if (opt.kind === 'preset') {
        commitPercent(opt.percent)
        inputRef.current?.blur()
        return
      }

      if (opt.kind === 'fit' && fitToSpreadDisabled) return
      if (opt.kind === 'zoomSelection' && zoomSelectionRowDisabled) return

      setIsDirty(false)
      dirtyFromInputChangeRef.current = false
      if (opt.kind === 'actualSize') {
        onActualSize()
      } else if (opt.kind === 'fit') {
        onZoomFit()
      } else {
        onZoomToSelection?.()
      }

      setOpen(false)
      setHighlightIndex(-1)
      inputRef.current?.blur()
    },
    [
      commitPercent,
      dirtyFromInputChangeRef,
      fitToSpreadDisabled,
      inputRef,
      menuOptions,
      onActualSize,
      onZoomFit,
      onZoomToSelection,
      setHighlightIndex,
      setIsDirty,
      setOpen,
      zoomSelectionRowDisabled,
    ]
  )

  return { commitDraft, activateOptionIndex }
}
